import React, { Fragment, useEffect, useRef, useState } from 'react';
import FullCalendar, {
  DayHeaderContentArg,
  EventApi,
  EventContentArg,
  EventHoveringArg,
  EventSourceInput,
} from '@fullcalendar/react';
import interactionPlugin, { Draggable } from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import moment from 'moment';
import {
  Box,
  Grid,
  Paper,
  Popper,
  Tooltip,
  Typography,
  css,
  useTheme,
} from '@mui/material';

import './style.css';
import { CalendarEvent } from './CalendarEvent';
import { CalendarHeader } from './CalendarHeader';
import { useBreakpointQuery } from '../../utils/media.util';
import { TableFilter } from '../table/TabIndexTableToolbar';
import { BackdropLoading } from '../BackdropLoading';
import { CalendarStage } from './CalendarStage';
import { DraggableCalendarEventWrapper } from './DraggableCalendarEventWrapper';
import { getDefaultTooltipByType } from '../items';

interface CalendarProps {
  header?: { expanded: boolean; setExpanded: Function };
  events?: EventSourceInput;
  items?: boolean;
  eventRender?: Function;
  eventClick?: Function;
  updateEvent?: Function;
  month?: boolean;
  filters?: TableFilter[][];
  setMonth?: Function;
  loading?: boolean;
  accordion?: boolean;
}

export const Calendar = ({
  header,
  events = [],
  items = false,
  eventRender = () => {},
  eventClick = () => {},
  updateEvent = () => {},
  month = false,
  setMonth = () => {},
  loading = false,
  filters,
  accordion = false,
}: CalendarProps) => {
  const smDown = useBreakpointQuery('sm-down');
  const [title, setTitle] = useState<string>(
    moment(new Date()).format('MMMM YYYY')
  );
  const [eventsWithoutDate, setEventsWithoutDate] = useState<any[]>(
    (events as any[])?.filter((e: any) => !e.start && !e.end)
  );
  const [eventsWithDate, setEventsWithDate] = useState<any[]>(
    (events as any[])?.filter((e: any) => e.start || e.end)
  );
  const theme = useTheme();
  const calendarRef = useRef<any>(null);

  useEffect(() => {
    if (Array.isArray(events)) {
      setEventsWithoutDate(
        (events as any[])?.filter((e: any) => !e.start && !e.end)
      );
      setEventsWithDate(
        (events as any[])?.filter((e: any) => e.start || e.end)
      );
    }
  }, [events]);

  useEffect(() => {
    if (calendarRef && Array.isArray(eventsWithDate)) {
      calendarRef.current._calendarApi.removeAllEvents();
      eventsWithDate?.forEach(element => {
        calendarRef.current._calendarApi.addEvent({
          ...element,
          editable: true,
        });
      });
    }
  }, [calendarRef, eventsWithDate]);
  const [popupEvent, setPopupEvent] = useState<EventHoveringArg | null>(null);

  // Event handler for eventMouseEnter
  const handleEventMouseEnter = (event: EventHoveringArg) => {
    setPopupEvent(event);
  };

  // Event handler for eventMouseLeave
  const handleEventMouseLeave = () => {
    setPopupEvent(null);
  };

  const renderDayHeaderContent = (props: DayHeaderContentArg) => {
    // More on dayHeaderContent: https://fullcalendar.io/docs/day-header-render-hooks

    const { date } = props;

    const isToday = moment.utc(date).format('dddd') === moment().format('dddd');
    const dateStr = moment.utc(date).format(smDown ? 'dd' : 'dddd');

    return (
      <Box
        sx={{
          paddingTop: 1.5,
          paddingBottom: 0.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: isToday
              ? theme.palette.primary.main
              : theme.palette.text.secondary,
            fontWeight: isToday ? 600 : 500,
            lineHeight: 1,
            letterSpacing: 0.1,
          }}
        >
          {dateStr}
        </Typography>
      </Box>
    );
  };

  const renderEventContent = (props: EventContentArg) => {
    // More on eventContent: https://fullcalendar.io/docs/event-render-hooks

    const { event } = props;
    if (false && event.extendedProps.item_type === 'Stage') {
      return (
        <CalendarStage
          title={event.title}
          icon={event?.extendedProps?.icon}
          type={event?.extendedProps?.type}
          completed={event?.extendedProps?.completed}
          count={event?.extendedProps?.count}
          id={event?.id}
        />
      );
    } else if (eventRender) {
      return (
        <Box
          sx={{
            overflow: 'clip',
            zoom: '75%',
            width: '100%',
            cursor: event?.extendedProps?.icons?.includes('task')
              ? 'pointer'
              : 'default',
            // '&:hover': {
            //   zIndex: 9999999,
            // },
            boxShadow: theme => theme.shadows[5],
          }}
        >
          {eventRender(event)}
        </Box>
      );
      // return (
      //   <DraggableCalendarEventWrapper>
      //     {eventRender(event)}
      //   </DraggableCalendarEventWrapper>
      // );
    } else if (event.extendedProps.item_type === 'Stage') {
      return (
        <CalendarStage
          title={event.title}
          icon={event?.extendedProps?.icon}
          type={event?.extendedProps?.type}
          completed={event?.extendedProps?.completed}
          count={event?.extendedProps?.count}
          id={event?.id}
        />
      );
    } else {
      return (
        <CalendarEvent
          title={event.title}
          icon={event?.extendedProps?.icon}
          type={event?.extendedProps?.type}
          completed={event?.extendedProps?.completed}
          count={event?.extendedProps?.count}
          id={event?.id}
        />
      );
    }
  };

  const Container = header ? CalendarHeader : Fragment;
  return (
    <div id={'calendar'} style={{ backgroundColor: 'white', zIndex: 99999 }}>
      {popupEvent && popupEvent.event?.extendedProps?.icons?.[0] && (
        <Popper
          open={Boolean(popupEvent)}
          anchorEl={popupEvent.el}
          placement="top"
          disablePortal
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
          ]}
          sx={{ zIndex: 9999999999, left: 0 }}
        >
          <Paper
            elevation={5}
            sx={{
              p: 1,
              bgcolor: theme => theme.palette.grey[700],
              color: 'white',
            }}
          >
            <Typography variant="body2">
              {getDefaultTooltipByType(
                popupEvent.event?.extendedProps?.icons?.[0],
                !!popupEvent.event?.start,
                !!popupEvent.event?.end
              )}
            </Typography>
            {/* <Typography variant="body2" sx={{ textDecoration: 'underline' }}>
              {popupEvent.event.title}
            </Typography> */}
          </Paper>
        </Popper>
      )}
      <Container
        {...header}
        accordion={accordion}
        filters={filters}
        onNext={() => {
          if (calendarRef?.current) {
            const calendarApi = calendarRef.current.getApi();
            calendarApi.next();
            setTitle(moment(calendarApi.view.currentStart).format('MMMM YYYY'));
            setMonth(
              moment(calendarApi.view.currentStart).format('YYYY-MM-DD')
            );
          }
        }}
        onPrev={() => {
          if (calendarRef?.current) {
            const calendarApi = calendarRef.current.getApi();
            calendarApi.prev();
            setTitle(moment(calendarApi.view.currentStart).format('MMMM YYYY'));
            setMonth(
              moment(calendarApi.view.currentStart).format('YYYY-MM-DD')
            );
          }
        }}
        onThisWeek={() => {
          if (calendarRef?.current) {
            const calendarApi = calendarRef.current.getApi();
            calendarApi.today();
            setTitle(moment(calendarApi.view.currentStart).format('MMMM YYYY'));
            setMonth(
              moment(calendarApi.view.currentStart).format('YYYY-MM-DD')
            );
          }
        }}
        title={title}
        month={month}
        children={
          <Box
            sx={{
              borderTop: `2px solid #E0E0E0`,
            }}
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            {loading && <BackdropLoading />}
            <Grid container direction={'row'}>
              {eventsWithoutDate &&
                (eventsWithoutDate as any[])?.length > 0 &&
                items && (
                  <Grid
                    md={2}
                    sx={{
                      border: theme =>
                        `5px solid ${theme.palette.secondary.main}`,
                      height: '85vh',
                      overflowY: 'scroll',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: 'white',
                        background: theme => theme.palette.secondary.main,
                        border: theme =>
                          `3px solid ${theme.palette.secondary.main}`,
                      }}
                    >
                      Stages/Items without date(s)
                    </Typography>
                    {eventsWithoutDate?.map((e: any) => (
                      <Box
                        onClick={() => eventClick(e.extendedProps)}
                        sx={{
                          py: 0.25,
                          px: 0.5,
                          cursor: 'pointer',
                          // overflowX: 'hidden',
                          // '&hover': {
                          //   overflowX: 'visible',
                          // },
                        }}
                      >
                        {renderEventContent({ event: e } as any)}
                      </Box>
                    ))}
                  </Grid>
                )}

              {/* // TODO: More on drag and drop details https://codesandbox.io/s/vm45zwmo07 */}
              <Grid
                md={
                  eventsWithoutDate &&
                  (eventsWithoutDate as any[])?.length > 0 &&
                  items
                    ? 10
                    : 12
                }
              >
                <FullCalendar
                  ref={calendarRef}
                  rerenderDelay={1}
                  nowIndicator={true}
                  plugins={[dayGridPlugin]}
                  headerToolbar={false}
                  initialView={
                    month
                      ? 'dayGridMonth'
                      : smDown
                      ? 'dayGridDay'
                      : 'dayGridWeek'
                  }
                  dayHeaderContent={renderDayHeaderContent}
                  // TODO: should be calculated dynamically
                  height={month ? '85vh' : '35vh'}
                  // contentHeight={'35%'}
                  weekends
                  // firstDay={0}
                  editable={false}
                  selectable={false}
                  selectMirror={false}
                  // eventTextColor="red"
                  droppable={false}
                  dayMaxEvents={true}
                  initialEvents={eventsWithDate}
                  eventContent={renderEventContent}
                  // TODO: Should be passed as props
                  // select={props => {
                  //   console.log('select', props);
                  // }}
                  // eventMouseEnter={event => console.log(event)}
                  eventMouseEnter={handleEventMouseEnter}
                  eventMouseLeave={handleEventMouseLeave}
                  eventClick={event => {
                    // console.log('eventclick');
                    eventClick(event.event.extendedProps);
                  }}
                  eventInteractive={false} //disallow click
                  // eventDrop={(props: any) => console.log('eventDrop', props)}
                  // eventReceive={(props: any) =>
                  //   console.log('eventReceive', props)
                  // }
                  // eventsSet={(props: any) => console.log('eventsSet', props)}
                  // eventAdd={(props: any) => console.log('eventAdd', props)}
                  // eventChange={(props: any) =>
                  //   console.log('eventChange', props)
                  // }
                  // eventRemove={(props: any) =>
                  //   console.log('eventRemove', props)
                  // }
                  // drop={async (props: any) => {
                  //   const event = (events as any[])?.filter(
                  //     e => e.id === props.draggedEl.dataset.id
                  //   )?.[0];
                  //   if (event) {
                  //     await updateEvent({
                  //       ...event,
                  //       start: null,
                  //       end: props.date,
                  //     });
                  //     calendarRef.current._calendarApi.addEvent({
                  //       ...event,
                  //       start: null,
                  //       end: props.date,
                  //     });
                  //     return;
                  //     setEventsWithoutDate(pv =>
                  //       pv?.filter(e => e.id !== props.draggedEl.dataset.id)
                  //     );
                  //     setEventsWithDate(pv => [
                  //       ...pv?.filter(e => e.id !== props.draggedEl.dataset.id),
                  //       {
                  //         ...event,
                  //         start: null,
                  //         end: props.date,
                  //       },
                  //     ]);
                  //   }
                  // }}
                />
              </Grid>
            </Grid>
          </Box>
        }
      />
    </div>
  );
};
